/**
 * #2377: what a family upload may be, mirrored from the server.
 *
 * The application and renewal upload routes (backend/src/routes/applications.js
 * and renewals.js) take JPEG, PNG and PDF up to 10 MB and refuse everything
 * else. The file picker's `accept` hides other files by default, but drag and
 * drop and the picker's "All files" still reach the upload path, so the tile
 * checks the file itself and names the problem beside it before any request.
 *
 * Reused by the receipt upload (#2380).
 */

import type { AnyTFunction } from '@/i18n/msg';

/** The MIME types the server accepts. `image/jpg` is non-standard but the
 *  server takes it, so the client does too. */
export const ACCEPTED_UPLOAD_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];

/** The file input's `accept` attribute: the standard types only. */
export const UPLOAD_ACCEPT = 'image/jpeg,image/png,application/pdf';

/** The server's per-file cap (multer `limits.fileSize`). */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** The cap in whole megabytes, for copy that names it. */
export const MAX_UPLOAD_MB = MAX_UPLOAD_BYTES / (1024 * 1024);

/** Why the server would refuse this file, or null when it would take it. */
export type UploadRefusal = 'type' | 'size';

/** The server's machine code for a refused file type (#2380, documents.js and reimbursements.js). */
export const UNSUPPORTED_FILE_TYPE = 'unsupported_file_type';

/** True when an upload failed because the server refused the file's type (#2380). */
export function isUnsupportedFileTypeError(err: unknown): boolean {
  return (err as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code === UNSUPPORTED_FILE_TYPE;
}

export function uploadRefusal(file: { type: string; size: number }): UploadRefusal | null {
  if (!ACCEPTED_UPLOAD_TYPES.includes(file.type)) return 'type';
  if (file.size > MAX_UPLOAD_BYTES) return 'size';
  return null;
}

/** #2473: a zero-byte file. The server would take it and store nothing a
 *  reviewer can open; a photo whose iCloud download never finished, or a
 *  file still syncing, arrives this way. Kept out of `uploadRefusal` so the
 *  receipt surfaces, which word only `type` and `size`, are unchanged. */
export function isEmptyUpload(file: { size: number }): boolean {
  return file.size === 0;
}

/**
 * #2473: why an upload that LEFT the browser failed, in the four words a
 * family can act on.
 *
 * - `size`: the server (multer's 10 MB limit, "File is too large…") or the
 *   proxy in front of it (a 413 from nginx's body cap) refused the bytes.
 * - `type`: the server refused the type (`unsupported_file_type`, or the
 *   application/renewal routes' "Invalid file type…" sentence, which carries
 *   no code).
 * - `network`: no answer came back at all — axios's `ERR_NETWORK`, a timeout,
 *   or a phone that went offline mid-upload.
 * - `failed`: anything else.
 */
export type UploadFailure = 'size' | 'type' | 'network' | 'failed';

interface AxiosLike {
  response?: { status?: unknown; data?: { code?: unknown; message?: unknown } };
  code?: unknown;
  message?: unknown;
}

const NETWORK_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET']);

export function uploadFailureKind(err: unknown): UploadFailure {
  const e = (err ?? null) as AxiosLike | null;
  const res = e?.response;
  if (!res) {
    if (NETWORK_CODES.has(String(e?.code)) || e?.message === 'Network Error') return 'network';
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'network';
    return 'failed';
  }
  const message = typeof res.data?.message === 'string' ? res.data.message : '';
  if (res.status === 413 || /too large/i.test(message)) return 'size';
  if (res.data?.code === UNSUPPORTED_FILE_TYPE || /invalid file type/i.test(message)) return 'type';
  return 'failed';
}

/**
 * #2473: the sentence a family reads on a failed upload's row.
 *
 * The four kinds above read from the catalog, in the family's language. The
 * one exception is a `failed` 4xx that carries a sentence of the server's own
 * (a slot already full, consent still needed): that is a reason the family can
 * act on and no catalog line says it, so an English reader still gets it
 * (#2109 R3-B11 — the server speaks English only). A 5xx, or a validation
 * dump, is never shown.
 */
export function uploadFailureText(t: AnyTFunction, err: unknown, language: string): string {
  const say = t as unknown as (key: string, params?: Record<string, unknown>) => string;
  const kind = uploadFailureKind(err);
  if (kind === 'size') return say('forms:documents.uploadErrorSize', { max: MAX_UPLOAD_MB });
  if (kind === 'type') return say('forms:documents.uploadErrorType');
  if (kind === 'network') return say('forms:documents.uploadErrorNetwork');
  const res = (err as AxiosLike | null)?.response;
  const status = typeof res?.status === 'number' ? res.status : undefined;
  const message = typeof res?.data?.message === 'string' ? res.data.message.trim() : '';
  const english = language === 'en' || language.startsWith('en-');
  const clientError = status === undefined || (status >= 400 && status < 500);
  if (english && clientError && message && !/^validation failed/i.test(message)) return message;
  return say('forms:documents.uploadFailedRow');
}
